import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, DialogContent, IconButton, Tab, Typography } from '@mui/material';
import { StyleAiDialog } from './AiDialog.style';
import { TabPanel } from '../TabPanel';
import { CloseRounded } from '@mui/icons-material';
import { RootState, useAppDispatch } from '~/store';
import {
  useGetResourcesQuery,
  useGetResourcesRecommendQuery,
  useUpdateRecommendSettingsMutation,
  // useLazyUpdateRecommendSettingsQuery,
} from '~/api/oneBoxV3';
import { resourceEduCode } from '~/api/oneBoxV3/oneBoxV3.type';
import {
  getterRecommendResourcesPayload,
  getterSingleLevelRecommend,
} from '~/store/aiAssistant/aiAssistantGetter';
import { getterClientResources } from '~/store/resource/resourceGetter';
import { setSearchCondition } from '~/store/resource';
import {
  setOpenSettingDialog,
  setRecommendMethod,
  setSelectedIndex,
} from '~/store/aiAssistant';
import { DEVICE, useDevice } from '~/utils/hook/useDevice';
import dayjs from 'dayjs';
import { StyleTabPanel } from '../TabPanel/TabPanel.style';

type TProps = {
  // RecommendSettingsData?: TOneBoxV3GetRecommendSettings;
  handleClose: () => void;
};

export enum RecommendMethod {
  SCHEDULED = 'scheduled',
  CUSTOMIZED = 'customized',
}

const recommendMethodColor = {
  scheduled: {
    background: '#FFB73D',
    tab: '#FFDFBA',
    activeTab: '#FFFCEB',
    text: '#637381',
    activeText: '#837300',
  },
  customized: {
    background: '#C8FAD1',
    tab: '#A3F1B1',
    activeTab: '#F3FFF4',
    text: '#637381',
    activeText: '#837300',
  },
};

const ScheduledTab = ({
  week,
  weekStart,
  weekEnd,
}: {
  week: number;
  weekStart: string;
  weekEnd: string;
}) => {
  const weekStartDay = dayjs(weekStart).format('MM/DD');
  const weekEndDay = dayjs(weekEnd).format('MM/DD');
  if (!week || !weekStart || !weekEnd) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <Typography variant="h5">課程計畫尚未開始</Typography>
      </Box>
    );
  }
  return (
    <Box textAlign="left">
      <Typography variant="h5">課程計畫推薦，適用範圍</Typography>
      <Typography
        variant="h5"
        component="div"
        display="flex"
        alignItems="center"
      >
        第
        <StyleAiDialog.DecoratedText>
          {('0' + week).slice(-2)}
        </StyleAiDialog.DecoratedText>
        週 |
        <StyleAiDialog.DecoratedText>
          {weekStartDay}
        </StyleAiDialog.DecoratedText>
        (日)~
        <StyleAiDialog.DecoratedText>{weekEndDay}</StyleAiDialog.DecoratedText>
        (六)
      </Typography>
    </Box>
  );
};

const CustomizedTab = ({
  week,
  weekStart,
  weekEnd,
}: {
  week: number;
  weekStart: string;
  weekEnd: string;
}) => {
  const weekStartDay = weekStart ? dayjs(weekStart).format('MM/DD') : '';
  const weekEndDay = weekEnd ? dayjs(weekEnd).format('MM/DD') : '';
  return (
    <Box textAlign="left">
      <Typography variant="h5">上次開啟週次</Typography>
      <Typography variant="h5">
        第 {('0' + week).slice(-2)} 週 | {weekStartDay}(日)~{weekEndDay}(六)
      </Typography>
    </Box>
  );
};

export const AiDialog = ({ handleClose }: TProps) => {
  const dispatch = useAppDispatch();
  const recommendSetting = useSelector(
    (state: RootState) => state.aiAssistant.recommendSetting,
  );
  const recommendMethod = useSelector(
    (state: RootState) => state.aiAssistant.recommendMethod,
  );
  const { displayWeekInfos, resourceConfigs } = recommendSetting;
  const {
    currentYear,
    currentSemester,
    currentWeek,
    weekStart,
    weekEnd,
    userDisplayWeek,
    userDisplayWeekEnd,
    userDisplayWeekStart,
    lastWeek,
  } = displayWeekInfos;
  const schoolYear =
    currentSemester === '1' ? `${currentYear}上` : `${currentYear}下`;
  const selectedIndex = useSelector(
    (state: RootState) => state.aiAssistant.selectedIndex,
  );
  const { device } = useDevice();
  const [updateRecommendSettings] = useUpdateRecommendSettingsMutation();
  // 取得推薦資源
  const recommendResourcesPayload = useSelector(
    getterRecommendResourcesPayload,
  );
  useGetResourcesRecommendQuery(recommendResourcesPayload);
  const singleLevelRecommend = useSelector(state =>
    getterSingleLevelRecommend(state, recommendResourcesPayload),
  );
  useEffect(() => {
    void (async () => {
      try {
        await updateRecommendSettings({
          weekAdjust: {
            userYear: currentYear || '',
            userSemester: currentSemester || '',
            userWeek: userDisplayWeek,
          },
        });
      } catch (error) {
        console.log('updateRecommendSettings error', error);
      }
    })();
  }, [userDisplayWeek]);

  // 取得全資源
  const resourcePayload = {
    search: {
      eduCodes:
        resourceConfigs === null
          ? resourceEduCode.E
          : (resourceConfigs[selectedIndex]?.eduCode as resourceEduCode),
      years: schoolYear,
      grades:
        resourceConfigs === null ? '' : resourceConfigs[selectedIndex]?.grade,
      subjects:
        resourceConfigs === null ? '' : resourceConfigs[selectedIndex]?.subject,
      chapterKeyword: '全',
    },
    isLaunch: true,
    pagination: {
      page: 1,
      pageSize: null,
      sort: null,
    },
  };
  useGetResourcesQuery(resourcePayload, {
    refetchOnMountOrArgChange: true,
    skip: !resourceConfigs,
  });
  const { resources } = useSelector(state =>
    getterClientResources(state, resourcePayload),
  );

  return (
    <StyleAiDialog.Dialog fullWidth onClose={() => {}} open={true}>
      <IconButton
        onClick={handleClose}
        sx={{
          color: 'white',
          position: 'absolute',
          top: '10px',
          right: '10px',
        }}
      >
        <CloseRounded />
      </IconButton>
      <StyleAiDialog.DialogTitle>
        <Typography
          variant="h3"
          component="div"
          sx={{
            wordBreak: 'keep-all',
            marginRight: '35px',
            marginBottom: 'spacing.1',
            width: device === DEVICE.MOBILE ? '100%' : 'auto',
            order: '1',
          }}
        >
          <span>AI 助教推薦資源 </span>
          <span>
            {currentYear}學年度
            {currentSemester == '1' ? ' 上' : ' 下'}學期
          </span>
        </Typography>
        <StyleAiDialog.SettingButton
          sx={{
            order: device === DEVICE.MOBILE ? '3' : '2',
            marginRight: device === DEVICE.MOBILE ? '0px' : '35px',
          }}
          onClick={() => {
            dispatch(setSearchCondition({ resources1: 'AI 助教' }));
            dispatch(setOpenSettingDialog(true));
            handleClose();
          }}
        >
          編輯設定
        </StyleAiDialog.SettingButton>
        <Typography
          variant="h5"
          color="primary.contrastText"
          width={device === DEVICE.MOBILE ? 'auto' : '100%'}
          order={device === DEVICE.MOBILE ? '2' : '3'}
          component={'div'}
        >
          選擇推薦方式
        </Typography>
      </StyleAiDialog.DialogTitle>
      <DialogContent sx={{ padding: '0px 8px 8px' }}>
        <StyleAiDialog.Tabs
          scrollButtons={false}
          isMobile={device === DEVICE.MOBILE}
          value={recommendMethod}
          onChange={(_e, value: RecommendMethod) =>
            dispatch(setRecommendMethod(value))
          }
          TabIndicatorProps={{
            style: { display: 'none' },
          }}
        >
          <Tab
            label={
              <ScheduledTab
                week={currentWeek}
                weekStart={weekStart}
                weekEnd={weekEnd}
              />
            }
            value={RecommendMethod.SCHEDULED}
          />
          <Tab
            label={
              <CustomizedTab
                week={userDisplayWeek}
                weekStart={userDisplayWeekStart}
                weekEnd={userDisplayWeekEnd}
              />
            }
            value={RecommendMethod.CUSTOMIZED}
          />
        </StyleAiDialog.Tabs>
        <Box
          sx={{
            background: recommendMethodColor[recommendMethod].background,
            height:
              device === DEVICE.MOBILE
                ? 'calc(100% - 195px)'
                : 'calc(100% - 100px)',
            borderRadius: '8px',
            padding: '8px',
          }}
        >
          <StyleAiDialog.SubTabs
            value={selectedIndex}
            onChange={(_e, value) => dispatch(setSelectedIndex(Number(value)))}
            TabIndicatorProps={{
              style: { display: 'none' },
            }}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            tabColor={recommendMethodColor[recommendMethod].tab}
            activeTabColor={recommendMethodColor[recommendMethod].activeTab}
            tabTextColor={recommendMethodColor[recommendMethod].text}
            activeTextColor={recommendMethodColor[recommendMethod].activeText}
          >
            {resourceConfigs &&
              resourceConfigs.map((recommend, index) => (
                <Tab
                  key={index}
                  value={index}
                  label={`${recommend.eduCode}${recommend.grade}${recommend.subject}`}
                />
              ))}
          </StyleAiDialog.SubTabs>
          {currentWeek === null &&
          recommendMethod === RecommendMethod.SCHEDULED ? (
            <StyleTabPanel.Container
              background={recommendMethodColor[recommendMethod].activeTab}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography variant="h6">課程計畫尚未開始</Typography>
              </Box>
            </StyleTabPanel.Container>
          ) : (
            <TabPanel
              RecommendResources={singleLevelRecommend}
              allRecommendResources={resources.slice(0, 10)}
              handleClose={handleClose}
              background={recommendMethodColor[recommendMethod].activeTab}
              recommendMethod={recommendMethod}
              userDisplayWeek={userDisplayWeek}
              lastWeek={lastWeek}
            />
          )}
        </Box>
      </DialogContent>
    </StyleAiDialog.Dialog>
  );
};
